/* @jsx h */
import { defineComponent, ref, onMounted, h } from "vue";
import { ShiftDetails as ShiftDetailsType } from "@lemmonhq/types";
import { PaymentMethods } from "../../Overview/components/PaymentMethods";
import { OrderTypes } from "../../Overview/components/OrderTypes";
import { SummaryStats } from "../../Overview/components/SummaryStats";
import { getDateRange, Loading } from "@app/vue/components/UIComponents";
import { reportsAPI } from "@app/data/api/reports";
import { RightSheet } from "@lemmonhq/uikit";
import { formatDate } from "@lemmonhq/orders";
import { useAuth } from "@app/vue/providers/Auth";

export type ShiftDetailsProps = {
  shift: ShiftDetailsType;
  isOpen: boolean;
  onClose: () => void;
};

export const ShiftDetails = defineComponent<ShiftDetailsProps>({
  name: "ShiftDetails",
  props: ["shift", "isOpen", "onClose"],
  setup(props) {
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";
    const loading = ref(true);
    const stats = ref<any>(null);
    const ordersBreakdown = ref<any>(null);
    const dateRange = getDateRange("today");

    const loadStats = async () => {
      if (!props.shift.id) return;

      loading.value = true;
      try {
        const [paymentStats, orderStats] = await Promise.all([
          reportsAPI.getPaymentsSummary({ p_shift_id: props.shift.id }),
          reportsAPI.getOrdersBreakdown(
            {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              type: "today" as any,
            },
            props.shift.id,
          ),
        ]);

        stats.value = paymentStats;
        ordersBreakdown.value = orderStats;
      } catch (error) {
        console.error("Failed to load shift stats:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadStats();
    });

    const formatActualShiftWindow = () => {
      const start = formatDate(props.shift.start);
      const end = props.shift.end ? ` to ${formatDate(props.shift.end)}` : "";
      return `Opened ${start}${end}`;
    };

    return () => (
      <RightSheet
        isOpen={props.isOpen}
        onClose={props.onClose}
        width="1000px"
        title={
          <div class="min-w-0 text-center lg:text-left">
            <div class="text-lg font-bold text-accent truncate">
              {props.shift.user?.name || "Customer App"}
            </div>
            <div class="text-xs text-accent/60 truncate">
              {formatDate(props.shift.scheduled_start)} ·{" "}
              {formatActualShiftWindow()}
            </div>
          </div>
        }
      >
        {loading.value ? (
          <div class="w-full flex justify-center py-8">
            <Loading />
          </div>
        ) : (
          stats.value &&
          ordersBreakdown.value && (
            <div class="space-y-6 p-4">
              <SummaryStats
                data={stats.value}
                ordersBreakdown={ordersBreakdown.value}
                currency={currency()}
              />
              <div class="grid gap-6">
                <OrderTypes
                  data={ordersBreakdown.value}
                  totalAmount={stats.value.total_amount}
                  currency={currency()}
                />
                <PaymentMethods data={stats.value} currency={currency()} />
              </div>
            </div>
          )
        )}
      </RightSheet>
    );
  },
});
